import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Paper, TextField, Button, Grid, Typography,
  Autocomplete, IconButton, Select, MenuItem, FormControl, InputLabel, Divider,
  Chip, Box, OutlinedInput, CircularProgress, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublishIcon from '@mui/icons-material/Publish';
import LinkIcon from '@mui/icons-material/Link';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import MenuWeekSection from './MenuWeekSection';
import { getDeliveryDate, toIsoDate } from './menuDateUtils';
import CustomerPreferencesPanel from './CustomerPreferencesPanel';
import MenuPreviewContent from './MenuPreviewContent';
import Menu from '../../models/menu';
import Users from '../../collections/users';
import Products from '../../collections/products';
import UserPackage from '../../models/user_package';

const weekdayOptions = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'Chủ nhật'];

const createDefaultWeeks = () => {
  const weeks = [];
  let deliveryNum = 1;
  for (let w = 1; w <= 4; w++) {
    const deliveries = [];
    for (let d = 0; d < 2; d++) {
      deliveries.push({
        delivery_number: deliveryNum,
        delivery_day: '',
        label: `Lần ${deliveryNum}`,
        total_weight: 0,
        sort_order: d,
        items: [],
      });
      deliveryNum++;
    }
    weeks.push({
      week_number: w,
      label: `Tuần ${w}`,
      sort_order: w - 1,
      deliveries,
    });
  }
  return weeks;
};

const MenuForm = ({ initialData, isEdit, setPageTitle }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publicUrl, setPublicUrl] = useState(initialData?.public_url || '');
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMeta, setGenerationMeta] = useState(null);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const userSearchTimer = useRef(null);

  const [menuData, setMenuData] = useState({
    user_id: null,
    user_package_id: null,
    title: '',
    subtitle: '',
    total_weight: 0,
    package_weight: 0,
    bonus_weight: 0,
    total_deliveries: 8,
    delivery_days: '',
    customer_name: '',
    customer_address: '',
    notes: [],
    status: 'DRAFT',
    cycle_start_date: null,
    weeks: createDefaultWeeks(),
    ...initialData,
  });

  useEffect(() => {
    setPageTitle(isEdit ? 'Sửa Menu KH' : 'Tạo Menu KH mới');
    fetchProducts();
  }, [setPageTitle, isEdit]);

  useEffect(() => {
    if (initialData && initialData.user) {
      setSelectedUser(initialData.user);
    }
  }, [initialData]);

  const searchUsers = (query) => {
    if (userSearchTimer.current) {
      clearTimeout(userSearchTimer.current);
    }
    if (!query || query.length < 2) {
      setUsers([]);
      return;
    }
    setUserSearchLoading(true);
    userSearchTimer.current = setTimeout(async () => {
      try {
        const usersCollection = new Users();
        await usersCollection.fetch(1, 20, {}, query);
        setUsers(usersCollection.items || []);
      } catch (err) {
        console.error('Error searching users:', err);
      } finally {
        setUserSearchLoading(false);
      }
    }, 300);
  };

  const fetchProducts = async () => {
    try {
      const productsCollection = new Products();
      await productsCollection.fetch('ACTIVE');
      setProducts(productsCollection.items || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  // Auto-calculate total converted weight
  const calcTotalWeight = useCallback(() => {
    let total = 0;
    menuData.weeks.forEach(week => {
      week.deliveries.forEach(delivery => {
        delivery.items.forEach(item => {
          if (item.item_type === 'FREE_GIFT') return;
          if (item.quantity && item.convert_rate) {
            total += item.quantity / item.convert_rate;
          } else if (item.weight_value) {
            total += parseFloat(item.weight_value);
          }
        });
      });
    });
    return Math.round(total * 100) / 100;
  }, [menuData.weeks]);

  // Count total deliveries
  const calcTotalDeliveries = useCallback(() => {
    return menuData.weeks.reduce((sum, w) => sum + w.deliveries.length, 0);
  }, [menuData.weeks]);

  const handleFieldChange = (field, value) => {
    setMenuData(prev => ({ ...prev, [field]: value }));
  };

  const handleCycleStartDateChange = (val) => {
    const dateStr = val ? val.format('YYYY-MM-DD') : null;
    setMenuData(prev => {
      const days = prev.delivery_days ? prev.delivery_days.split(' & ').filter(Boolean) : [];
      if (days.length === 0) return { ...prev, cycle_start_date: dateStr };
      return {
        ...prev,
        cycle_start_date: dateStr,
        weeks: rebuildWeeks(prev.weeks, days, dateStr, 4 * days.length),
      };
    });
  };

  const handleUserChange = async (_, user) => {
    setSelectedUser(user);
    if (user) {
      const updates = {
        user_id: user.id,
        customer_name: user.name || '',
        customer_address: user.address || '',
      };

      try {
        const userPackageModel = new UserPackage();
        const activePackage = await userPackageModel.getActivePackageByUserId(user.id);
        if (activePackage) {
          const pkg = activePackage.package || {};
          updates.user_package_id = activePackage.id;
          updates.package_weight = pkg.amount || 0;
          updates.bonus_weight = activePackage.bonus_total || 0;
          updates.total_deliveries = pkg.limit_delivery || 8;
          updates.title = `Menu Rau — KH ${user.name || ''}`;
          updates.subtitle = `Gói tháng ${pkg.amount || 0}kg + ${activePackage.bonus_total || 0}kg rau tặng`;
        }
      } catch (err) {
        console.error('Error fetching active package:', err);
      }

      setMenuData(prev => ({ ...prev, ...updates }));
    }
  };

  const handleWeekChange = (weekIdx, updatedWeek) => {
    setMenuData(prev => {
      const newWeeks = [...prev.weeks];
      newWeeks[weekIdx] = updatedWeek;
      return { ...prev, weeks: newWeeks };
    });
  };

  const rebuildWeeks = (existingWeeks, days, cycleStartDate, targetDeliveries) => {
    if (days.length === 0) return existingWeeks;
    const calcMaxWeeks = 10;

    // Step 1: generate exactly targetDeliveries dates, grouped by week index
    const deliverySlots = [];
    for (let w = 0; w < calcMaxWeeks && deliverySlots.length < targetDeliveries; w++) {
      for (const day of days) {
        if (deliverySlots.length >= targetDeliveries) break;
        const date = getDeliveryDate(cycleStartDate, w, calcMaxWeeks, day);
        if (!date && cycleStartDate) continue;
        deliverySlots.push({ weekIdx: w, day, date });
      }
    }

    // Step 2: group by weekIdx
    const weekMap = new Map();
    for (const slot of deliverySlots) {
      if (!weekMap.has(slot.weekIdx)) weekMap.set(slot.weekIdx, []);
      weekMap.get(slot.weekIdx).push(slot);
    }

    // Step 3: build weeks, reusing existing data where possible
    const weeks = [];
    for (const [wIdx, slots] of weekMap) {
      const existing = wIdx < existingWeeks.length ? existingWeeks[wIdx] : null;
      const locked = existing ? existing.deliveries.filter(d => d.has_orders) : [];
      const unlocked = existing ? existing.deliveries.filter(d => !d.has_orders) : [];

      const updatedUnlocked = slots.map((slot, idx) => {
        const base = idx < unlocked.length
          ? unlocked[idx]
          : { delivery_number: 0, label: '', total_weight: 0, sort_order: 0, items: [] };
        return { ...base, delivery_day: slot.day, delivery_date: toIsoDate(slot.date) };
      });

      weeks.push({
        ...(existing || { week_number: weeks.length + 1, label: `Tuần ${weeks.length + 1}`, sort_order: weeks.length }),
        deliveries: [...locked, ...updatedUnlocked],
      });
    }

    return weeks;
  };

  const handleDeliveryDaysChange = (newDays) => {
    const newDaysStr = newDays.join(' & ');
    setMenuData(prev => ({
      ...prev,
      delivery_days: newDaysStr,
      weeks: rebuildWeeks(prev.weeks, newDays, prev.cycle_start_date, 4 * newDays.length),
    }));
  };

  const parsedDeliveryDays = menuData.delivery_days
    ? menuData.delivery_days.split(' & ').filter(Boolean)
    : [];

  const handleAddNote = () => {
    setMenuData(prev => ({ ...prev, notes: [...prev.notes, ''] }));
  };

  const handleNoteChange = (idx, value) => {
    setMenuData(prev => {
      const newNotes = [...prev.notes];
      newNotes[idx] = value;
      return { ...prev, notes: newNotes };
    });
  };

  const handleRemoveNote = (idx) => {
    setMenuData(prev => ({ ...prev, notes: prev.notes.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    // Validation
    if (!menuData.user_id) {
      toast.error('Vui lòng chọn khách hàng');
      return;
    }
    if (!menuData.title) {
      toast.error('Vui lòng nhập tiêu đề');
      return;
    }
    if (!menuData.customer_name) {
      toast.error('Vui lòng nhập tên khách hàng');
      return;
    }

    setSaving(true);
    try {
      const menuModel = new Menu({
        ...menuData,
        total_weight: calcTotalWeight(),
        total_deliveries: calcTotalDeliveries(),
      });

      let response;
      if (isEdit && initialData?.id) {
        response = await menuModel.updateMenu(initialData.id);
      } else {
        response = await menuModel.createMenu();
      }

      if (response.success) {
        toast.success(response.message);
        navigate('/menus');
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      toast.error('Có lỗi xảy ra khi lưu menu');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!isEdit || !initialData?.id) {
      toast.error('Vui lòng lưu menu trước khi xuất');
      return;
    }
    setPublishing(true);
    try {
      const menuModel = new Menu();
      const result = await menuModel.publishMenu(initialData.id);
      if (result.success) {
        setPublicUrl(result.url);
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error('Có lỗi xảy ra khi xuất menu');
    } finally {
      setPublishing(false);
    }
  };

  const handleRegenerateOrder = async (deliveryId) => {
    try {
      const menuModel = new Menu();
      const result = await menuModel.regenerateOrder(initialData.id, deliveryId);
      if (result.success) {
        toast.success(result.message);
        // Reload menu data to refresh has_orders flags
        const updatedData = await menuModel.getMenuById(initialData.id);
        if (updatedData) {
          setMenuData(prev => ({ ...prev, weeks: updatedData.weeks || prev.weeks }));
        }
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error('Có lỗi xảy ra khi tạo lại đơn hàng');
    }
  };

  const hasMenuItems = menuData.weeks.some(w => w.deliveries.some(d => d.items.length > 0));

  const handleGenerateClick = () => {
    if (hasMenuItems) {
      setShowOverwriteDialog(true);
    } else {
      handleGenerate();
    }
  };

  const handleGenerate = async () => {
    setShowOverwriteDialog(false);
    setIsGenerating(true);
    try {
      const menuModel = new Menu();
      const result = await menuModel.generateMenu(
        menuData.user_id,
        menuData.user_package_id,
        additionalInstructions || null,
      );
      if (result.success) {
        const generated = result.data;
        setMenuData(prev => {
          // Collect all AI-generated items across all deliveries
          const aiItems = [];
          (generated.weeks || []).forEach(week => {
            (week.deliveries || []).forEach(delivery => {
              aiItems.push(...(delivery.items || []));
            });
          });

          // Distribute AI items into existing delivery structure
          let itemIdx = 0;
          const itemsPerDelivery = prev.weeks.reduce((sum, w) => sum + w.deliveries.length, 0);
          const avgItems = itemsPerDelivery > 0 ? Math.ceil(aiItems.length / itemsPerDelivery) : aiItems.length;

          const weeks = prev.weeks.map(week => ({
            ...week,
            deliveries: week.deliveries.map(delivery => {
              const chunk = aiItems.slice(itemIdx, itemIdx + avgItems);
              itemIdx += avgItems;
              return { ...delivery, items: chunk };
            }),
          }));

          return {
            ...prev,
            title: generated.title || prev.title,
            subtitle: generated.subtitle || prev.subtitle,
            total_weight: generated.total_weight || prev.total_weight,
            package_weight: generated.package_weight || prev.package_weight,
            bonus_weight: generated.bonus_weight ?? prev.bonus_weight,
            customer_name: generated.customer_name || prev.customer_name,
            customer_address: generated.customer_address || prev.customer_address,
            weeks,
          };
        });
        setGenerationMeta(result.metadata);
        const timeSeconds = (result.metadata?.generation_time_ms / 1000).toFixed(1);
        toast.success(`Tạo menu thành công (${timeSeconds}s)`);
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error('Có lỗi xảy ra khi tạo menu AI');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyUrl = () => {
    if (publicUrl) {
      navigator.clipboard.writeText(publicUrl);
      toast.success('Đã sao chép link');
    }
  };

  // Menus at COMPLETED are locked for edit. Staff must revert status to ACTIVE
  // (or DRAFT) in the header to re-enable Save. The backend enforces the same rule.
  const isCompletedLocked = isEdit
    && initialData?.status === 'COMPLETED'
    && menuData.status === 'COMPLETED';

  const formContent = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {isCompletedLocked && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Menu đã hoàn thành — không thể chỉnh sửa. Đổi trạng thái sang "Đang dùng" để chỉnh sửa nội dung.
        </Alert>
      )}
      {/* Header section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Thông tin chung</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(opt) => opt.name || ''}
              value={selectedUser}
              onChange={handleUserChange}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  searchUsers(value);
                }
              }}
              loading={userSearchLoading}
              loadingText="Đang tìm..."
              noOptionsText="Nhập tên để tìm khách hàng"
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Khách hàng *"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {userSearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small"
              label="Tiêu đề *"
              value={menuData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="Menu Rau — KH Lan Anh Nguyễn"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth size="small"
              label="Phụ đề"
              value={menuData.subtitle}
              onChange={(e) => handleFieldChange('subtitle', e.target.value)}
              placeholder="Gói tháng 16kg + 2kg rau tặng"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Ngày giao</InputLabel>
              <Select
                multiple
                value={parsedDeliveryDays}
                onChange={(e) => handleDeliveryDaysChange(e.target.value)}
                input={<OutlinedInput label="Ngày giao" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => <Chip key={val} label={val} size="small" />)}
                  </Box>
                )}
              >
                {weekdayOptions.map((day) => (
                  <MenuItem key={day} value={day}>{day}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Ngày bắt đầu chu kỳ"
              value={menuData.cycle_start_date ? dayjs(menuData.cycle_start_date) : null}
              onChange={handleCycleStartDateChange}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
              format="DD/MM/YYYY"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth size="small" type="number"
              label="KL gói (kg)"
              value={menuData.package_weight}
              onChange={(e) => handleFieldChange('package_weight', parseFloat(e.target.value) || 0)}
              inputProps={{ step: 0.5, min: 0 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth size="small" type="number"
              label="KL tặng (kg)"
              value={menuData.bonus_weight}
              onChange={(e) => handleFieldChange('bonus_weight', parseFloat(e.target.value) || 0)}
              inputProps={{ step: 0.5, min: 0 }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth size="small"
              label="Tên KH"
              value={menuData.customer_name}
              onChange={(e) => handleFieldChange('customer_name', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Trạng thái</InputLabel>
              <Select
                value={menuData.status}
                label="Trạng thái"
                onChange={(e) => handleFieldChange('status', e.target.value)}
              >
                <MenuItem value="DRAFT">Nháp</MenuItem>
                <MenuItem value="ACTIVE">Đang dùng</MenuItem>
                <MenuItem value="COMPLETED">Đã hoàn thành</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small"
              label="Địa chỉ KH"
              value={menuData.customer_address}
              onChange={(e) => handleFieldChange('customer_address', e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Customer Preferences */}
      {menuData.user_id && (
        <CustomerPreferencesPanel
          userId={menuData.user_id}
          onPreferencesLoaded={setHasPreferences}
        />
      )}

      {/* AI Generate Button */}
      {menuData.user_id && (
        <Paper sx={{ p: 2, mb: 3 }}>
          {generationMeta && (
            <Typography variant="body2" color="success.main" sx={{ mb: 1.5 }}>
              Menu tạo bởi AI ({(generationMeta.generation_time_ms / 1000).toFixed(1)}s)
              {generationMeta.products_unmatched > 0 && (
                <Typography component="span" variant="body2" color="warning.main" sx={{ ml: 1 }}>
                  — {generationMeta.products_unmatched} sản phẩm không khớp
                </Typography>
              )}
            </Typography>
          )}
          {generationMeta && (
            <TextField
              fullWidth size="small"
              label="Yêu cầu thêm cho AI (tùy chọn)"
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              placeholder="VD: Thêm nhiều rau salad hơn, bớt rau củ..."
              sx={{ mb: 1.5 }}
            />
          )}
          <Tooltip title={isCompletedLocked ? 'Đổi trạng thái sang "Đang dùng" để chỉnh sửa' : ''}>
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={isGenerating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={handleGenerateClick}
                disabled={!hasPreferences || isGenerating || isCompletedLocked}
              >
                {isGenerating
                  ? 'Đang tạo menu... (có thể mất 15-30 giây)'
                  : generationMeta ? 'Tạo lại menu (AI)' : 'Tạo menu tự động (AI)'}
              </Button>
            </span>
          </Tooltip>
        </Paper>
      )}

      {/* Overwrite confirmation dialog */}
      <Dialog open={showOverwriteDialog} onClose={() => setShowOverwriteDialog(false)}>
        <DialogTitle>Tạo menu mới?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Menu hiện tại sẽ bị thay thế bởi menu AI. Bạn có muốn tiếp tục?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOverwriteDialog(false)}>Hủy</Button>
          <Button onClick={handleGenerate} variant="contained" color="secondary">Tiếp tục</Button>
        </DialogActions>
      </Dialog>



      {/* Week sections */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Typography variant="h6">
            Lịch giao hàng — Tổng: {calcTotalWeight()} / {menuData.package_weight + menuData.bonus_weight} kg — {calcTotalDeliveries()} lần
          </Typography>
        </div>
        {menuData.weeks.map((week, idx) => {
          const deliveryStartIndex = menuData.weeks
            .slice(0, idx)
            .reduce((sum, w) => sum + w.deliveries.length, 0);
          return (
            <MenuWeekSection
              key={idx}
              week={week}
              weekIdx={idx}
              onChange={(updated) => handleWeekChange(idx, updated)}
              products={products}
              deliveryStartIndex={deliveryStartIndex}
              deliveryDays={parsedDeliveryDays}
              cycleStartDate={menuData.cycle_start_date}
              totalWeeks={menuData.weeks.length}
              menuId={initialData?.id}
              menuStatus={menuData.status}
              onRegenerateOrder={isEdit ? handleRegenerateOrder : undefined}
            />
          );
        })}
      </Paper>

      {/* Notes */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Ghi chú</Typography>
        {menuData.notes.map((note, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <TextField
              fullWidth size="small"
              value={note}
              onChange={(e) => handleNoteChange(idx, e.target.value)}
              placeholder="Nhập ghi chú..."
            />
            <IconButton size="small" onClick={() => handleRemoveNote(idx)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
        <Button startIcon={<AddIcon />} size="small" onClick={handleAddNote}>
          Thêm ghi chú
        </Button>
      </Paper>

      {/* Published URL */}
      {publicUrl && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#e8f5e9' }}>
          <LinkIcon color="success" fontSize="small" />
          <TextField
            size="small"
            value={publicUrl}
            fullWidth
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Sao chép link">
                    <IconButton size="small" onClick={handleCopyUrl}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Button size="small" variant="text" href={publicUrl} target="_blank">
            Mở
          </Button>
        </Paper>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <Button variant="outlined" onClick={() => navigate('/menus')}>Hủy</Button>
        {isEdit && initialData?.id && (
          <Button
            variant="outlined"
            color="success"
            startIcon={<PublishIcon />}
            onClick={handlePublish}
            disabled={publishing}
          >
            {publishing ? 'Đang xuất...' : 'Xuất & Chia sẻ'}
          </Button>
        )}
        <Tooltip title={isCompletedLocked ? 'Đổi trạng thái sang "Đang dùng" để chỉnh sửa' : ''}>
          <span>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || isCompletedLocked}
            >
              {saving ? 'Đang lưu...' : 'Lưu menu'}
            </Button>
          </span>
        </Tooltip>
      </div>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 4, px: 2 }}>
      {/* Left: Form */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        {formContent}
      </Box>
      {/* Right: Live Preview */}
      <Box sx={{
        flex: 1,
        minWidth: 0,
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        maxHeight: '100vh',
        overflow: 'auto',
        borderLeft: '1px solid #e0e0e0',
      }}>
        <Typography variant="subtitle2" sx={{ p: 1.5, borderBottom: '1px solid #e0e0e0', fontWeight: 600, bgcolor: '#f5f5f5' }}>
          Xem trước
        </Typography>
        <MenuPreviewContent menuData={menuData} />
      </Box>
    </Box>
  );
};

export default MenuForm;
